import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Star, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending", amount: 0, currency: "myr" });
  const [attempts, setAttempts] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const { api, refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }
    let cancelled = false;
    let tid;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status === "paid") {
          await refresh();
          toast.success("Payment confirmed. Welcome, Founding Wanderer.");
          return;
        }
        if (attempts >= 20) {
          setTimedOut(true);
          return;
        }
        setAttempts((a) => a + 1);
        tid = setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) {
          setTimedOut(true);
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (tid) clearTimeout(tid);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const paid = status.payment_status === "paid";
  const amount = ((status.amount || 0) / 100).toFixed(2);

  return (
    <section
      className="pt-[130px] md:pt-[150px] pb-24 md:pb-32 min-h-[80vh]"
      style={{ background: "var(--surface)" }}
      data-testid="payment-success"
    >
      <div className="wrap max-w-[720px] mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-extrabold tracking-[0.16em] uppercase mb-6"
          style={{ background: "var(--surface-secondary)", color: "var(--brand)", border: "1px solid var(--border-c)" }}>
          Founding Member
        </div>

        {!paid && !timedOut && (
          <>
            <Loader2 className="w-14 h-14 mx-auto animate-spin mb-6" style={{ color: "var(--brand)" }} />
            <h1 className="text-3xl md:text-5xl font-extrabold mb-4" style={{ color: "var(--on-surface)" }}>
              Confirming your payment…
            </h1>
            <p className="body-lg max-w-lg mx-auto">
              Hang tight — Stripe is just settling the deposit.
            </p>
          </>
        )}

        {paid && (
          <>
            <div className="mx-auto mb-8 w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: "color-mix(in srgb, var(--brand) 15%, transparent)", color: "var(--brand)" }}>
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold mb-4" style={{ color: "var(--on-surface)" }}>
              You're in, Founding Wanderer.
            </h1>
            <p className="body-lg max-w-lg mx-auto mb-6">
              Your <strong style={{ color: "var(--on-surface)" }}>RM{amount}</strong> Founding Member deposit
              is confirmed. A Lifetime Explorer slot is reserved for you when
              the app launches — check your inbox for a welcome from Tommy &amp; Koboi.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold"
              style={{ background: "var(--surface-secondary)", color: "var(--on-surface)", border: "1px solid var(--border-c)" }}
              data-testid="premium-confirmation">
              <Star className="w-4 h-4" fill="#FCD34D" stroke="#FCD34D" />
              Founding Member badge active
            </div>
            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <Link to="/" className="btn btn-green" data-testid="cta-home">
                Back to trailhead <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/pricing" className="btn btn-light" data-testid="cta-pricing">
                View plans
              </Link>
            </div>
          </>
        )}

        {timedOut && !paid && (
          <>
            <h1 className="text-3xl md:text-4xl font-extrabold mb-4" style={{ color: "var(--on-surface)" }}>
              Still processing…
            </h1>
            <p className="body-lg max-w-lg mx-auto mb-6">
              Stripe hasn't confirmed the payment yet. You'll get an email
              once it lands. Feel free to close this tab.
            </p>
            <Link to="/" className="btn btn-green">Back to trailhead</Link>
          </>
        )}
      </div>
    </section>
  );
}
